import EventRace from '@/orm/models/EventRace'
import { encodeBoatClass } from '@/network/decoders'
import BoatClass from '@/orm/models/BoatClass'
import Event from '@/orm/models/Event'
import { DateFormat, ds, ts } from '@/plugins/i18nFormat'
import EventBoat from '@model/EventBoat'
import EventCategory from '@model/EventCategory'
import Sandbox from '@nyariv/sandboxjs'
import { Model, useRepo } from 'pinia-orm'
import { DateCast } from 'pinia-orm/casts'
import { Attr, BelongsTo, Bool, Cast, HasMany, Num, OnDelete, Str, Uid } from 'pinia-orm/decorators'
import EventCoefficientsRepo from '@/orm/repositories/EventCoefficientsRepo'

import EventCoefficient from '@/orm/models/EventCoefficient'

export default class EventClass extends Model {
    static entity = 'events-classes'

    @Uid()
    declare id: string
    @Uid()
    declare eventId: string
    @BelongsTo(() => Event, 'eventId')
    declare event: Event
    @Num(0, { notNullable: true })
    declare boatClassId: number
    @BelongsTo(() => BoatClass, 'boatClassId')
    declare boatClass: BoatClass
    @Num(0, { notNullable: true })
    declare weight: number
    @Str(null)
    declare eventType: string | null
    @Str('', { notNullable: true })
    declare shortcut: string
    @Str('R4, R8')
    declare discardDefinition: string
    @HasMany(() => EventCategory, 'eventClassId') @OnDelete('cascade')
    declare categories: EventCategory[]
    @Str(null)
    declare className: string | null
    @Num(null)
    declare publicLicenseExceptionType: number | null
    @Num(null)
    declare days: number | null
    @Cast(() => DateCast) @Attr(null)
    declare startDate: string | null
    @Str(null)
    declare handicapSystem: string | null
    @Str(null)
    declare raceMode: string | null
    @Num(2, { notNullable: true })
    declare eliminationHeats: number
    @Num(2, { notNullable: true })
    declare eliminationRounds: number
    @Str('sailNo', { notNullable: true })
    declare eliminationStartSortMode: string
    @Str('blocks', { notNullable: true })
    declare eliminationStartDivisionMode: string
    @Str('lessFirst', { notNullable: true })
    declare eliminationRoundDistributionMode: string
    @Str('', { notNullable: true })
    declare eliminationHeatSizes: string
    @Str('snake', { notNullable: true })
    declare eliminationRaceDistributionMode: string
    @Bool(true)
    declare eliminationUseFinalGoldenNames: boolean
    @Str('ordered', { notNullable: true })
    declare eliminationHeatIdentStyle: string
    @Bool(false)
    declare isLongDistance: boolean
    @Bool(false)
    declare bestTimeResults: boolean
    @Num(10, { notNullable: true })
    declare racePenaltyTime: number

    static __cacheMap: Map<string, number> = new Map()

    static clearCache() {
        EventClass.__cacheMap.clear()
    }

    get dump() {
        return {
            boatClassId: encodeBoatClass(this.boatClassId),
            weight: this.weight,
            eventType: this.eventType,
            shortcut: this.shortcut,
            discardDefinition: this.discardDefinition,
            className: this.className,
            publicLicenseExceptionType: this.publicLicenseExceptionType,
            days: this.days,
            startDate: this.startDate !== null ? ds(this.startDate, DateFormat.SystemDate) : null,
            categories: this.categories.map((category) => category.dump),
            handicapSystem: this.handicapSystem,
            raceMode: this.raceMode,
            eliminationHeats: this.eliminationHeats,
            eliminationRounds: this.eliminationRounds,
            eliminationStartSortMode: this.eliminationStartSortMode,
            eliminationStartDivisionMode: this.eliminationStartDivisionMode,
            eliminationRoundDistributionMode: this.eliminationRoundDistributionMode,
            eliminationHeatSizes: this.eliminationHeatSizes,
            eliminationRaceDistributionMode: this.eliminationRaceDistributionMode,
            eliminationUseFinalGoldenNames: this.eliminationUseFinalGoldenNames,
            eliminationHeatIdentStyle: this.eliminationHeatIdentStyle,
            isLongDistance: this.isLongDistance,
            bestTimeResults: this.bestTimeResults,
            racePenaltyTime: this.racePenaltyTime,
        }
    }

    get isHandicapSelectable() {
        return this.handicapSystem === 'RsT' || this.handicapSystem === 'RsA' || this.handicapSystem === 'pylist'
    }

    get isHandicapByValue() {
        return this.handicapSystem === 'zadar' || this.handicapSystem === 'texel' || this.handicapSystem === 'pynumber'
    }

    get isElimination() {
        return this.raceMode === 'elimination' && this.handicapSystem === null
    }

    /**
     * Suggest a "heat-size" advancement key (comma-separated advancer counts terminated by a star).
     * The numbers are advancing places taken from each source heat, so they split one heat's worth of
     * boats - the largest heat holds ceil(total / heats) - across the `heats` destination groups: the
     * per-group counts differ by at most one, the smaller groups come first and the larger ones later,
     * and the last (largest) group is left as the star. Every group gets at least one place.
     * E.g. 32 competitors in 3 heats -> a heat of 11 split as 3+4+4 -> "3,4,*".
     */
    static suggestHeatSizes(total: number, heats: number): string {
        if (heats < 2)
            return '*'

        // Split one heat's boats across the groups; keep at least one per group when boats are scarce.
        const heatSize = Math.max(Math.ceil(total / heats), heats)
        const base = Math.floor(heatSize / heats)
        const extra = heatSize % heats

        const sizes: number[] = []
        for (let h = 0; h < heats; h++)
            sizes.push(base + (h >= heats - extra ? 1 : 0))

        // Drop the final (largest) group - it is represented by the trailing star.
        return sizes.slice(0, heats - 1).join(',') + ',*'
    }

    /**
     * Default "heat-size" advancement key for this class, i.e. the even split of the whole start
     * field across the heats (see {@link suggestHeatSizes}). Used to prefill the configuration field
     * and as the effective key while nothing has been entered manually.
     */
    defaultEliminationHeatSizes(): string {
        return EventClass.suggestHeatSizes(this.totalBoats, this.eliminationHeats)
    }

    /**
     * The advancement key actually in effect for the heat-size mode: the stored value, or the
     * lessFirst default when nothing has been entered yet (an empty field means "use the default").
     */
    get eliminationHeatSizesEffective(): string {
        const stored = (this.eliminationHeatSizes ?? '').trim()

        return stored !== '' ? stored : this.defaultEliminationHeatSizes()
    }

    /**
     * Whether the elimination advancement key is correctly configured. Only the "heat-size" mode
     * carries a free-form key that can be malformed; every other mode is always valid.
     */
    get isEliminationHeatSizesValid(): boolean {
        if (this.eliminationRoundDistributionMode !== 'heat-size')
            return true

        return EventClass.validateHeatSizes(this.eliminationHeatSizesEffective, this.eliminationHeats)
    }

    /**
     * The advancer counts of the heat-size key as plain numbers (without the trailing star), in group
     * order. E.g. "3,4,*" -> [3, 4].
     */
    get eliminationHeatSizeCounts(): number[] {
        const key = this.eliminationHeatSizesEffective

        return key.slice(0, key.lastIndexOf(',')).split(',').map((n) => parseInt(n.trim(), 10))
    }

    /**
     * How many rank blocks each destination group receives when advancing between rounds under the
     * heat-size key. Group k takes the count listed at position k; the last group absorbs every
     * remaining block. A rank block holds one boat per source heat, so the count is equally the number
     * of boats advancing from each source heat into that group. `totalBlocks` is the number of ranks
     * (places) available in the finished source round.
     */
    heatSizeGroupBlockCounts(totalBlocks: number): number[] {
        const heats = this.eliminationHeats
        const counts = this.eliminationHeatSizeCounts

        const result: number[] = []
        let used = 0

        for (let k = 0; k < heats; k++) {
            const count = (k < heats - 1) ? (counts[k] || 0) : Math.max(0, totalBlocks - used)
            result.push(count)
            used += count
        }

        return result
    }

    /**
     * Validate a heat-size key: comma-separated positive integers terminated by a star after a comma,
     * where the count of numbers plus the star equals the number of heats.
     */
    static validateHeatSizes(value: string, heats: number): boolean {
        const key = (value ?? '').trim()

        // Shape: one or more positive integers, then a trailing ",*".
        if (!/^\d+(\s*,\s*\d+)*\s*,\s*\*$/.test(key))
            return false

        const numbers = key.slice(0, key.lastIndexOf(',')).split(',').map((part) => part.trim())

        if (numbers.some((part) => parseInt(part, 10) < 1))
            return false

        return numbers.length + 1 === heats
    }

    /**
     * Human-readable name of an elimination heat (group) in a given round.
     *
     * @param round 1-based round number
     * @param heat  1-based heat number within the round (1..eliminationHeats)
     */
    eliminationHeatName(round: number, heat: number): string {
        const heats = this.eliminationHeats

        // In the last round the top heats may be named after precious metals.
        if (round === this.eliminationRounds && this.eliminationUseFinalGoldenNames) {
            if (heat === 1) return ts('heatGold')
            if (heat === 2) return ts('heatSilver')
            if (heat === 3) return ts('heatBronze')
        }

        // "round-heat" identifies a heat as K.X (round . heat within round).
        if (this.eliminationHeatIdentStyle === 'round-heat')
            return ts('heatNo', { no: round + '.' + heat })

        // "ordered" numbers heats continuously across rounds: round 1 -> 1..N, round 2 -> N+1..2N, ...
        return ts('heatNo', { no: (round - 1) * heats + heat })
    }

    /**
     * Sort the whole start field of the class according to eliminationStartSortMode.
     */
    sortEliminationStartField(boats: EventBoat[]): EventBoat[] {
        const sorted = [...boats]

        if (this.eliminationStartSortMode === 'captainName') {
            // Reverse name: last name, first name, then birth year.
            return sorted.sort((a, b) => {
                const ca = a.captain, cb = b.captain
                const last = (ca?.lastName ?? '').localeCompare(cb?.lastName ?? '')
                if (last !== 0) return last
                const first = (ca?.firstName ?? '').localeCompare(cb?.firstName ?? '')
                if (first !== 0) return first
                return (ca?.birthYear ?? 0) - (cb?.birthYear ?? 0)
            })
        }

        if (this.eliminationStartSortMode === 'random') {
            // Deterministic pseudo-random order - stable across renders, derived from the boat id.
            return sorted.sort((a, b) => EventClass.__idHash(a.id) - EventClass.__idHash(b.id))
        }

        // 'sailNo' (default): by the plain sail number, ignoring the national code.
        return sorted.sort((a, b) => (parseInt(a.sailNumber) || 0) - (parseInt(b.sailNumber) || 0))
    }

    /**
     * Distribution of the class start field into the first-round heats (0-based array,
     * index = heat number - 1). Only valid for the first round of the first race, where
     * the seeding derives purely from the entry list (no prior results yet).
     *
     * Group sizes never differ by more than one; lower-numbered heats receive the extra boat.
     */
    eliminationFirstRoundHeats(): EventBoat[][] {
        return this.distributeIntoHeats(this.sortEliminationStartField(this.boats), this.eliminationStartDivisionMode)
    }

    /**
     * Distribute an already-ordered list of boats into the heats (0-based array, index = heat - 1).
     * Supports 'round-robin' (1->h1, 2->h2, .., N->hN, N+1->h1, ..), 'snake' (1,2,..,N,N,..,2,1,.. )
     * and 'blocks' (consecutive chunks, lower-numbered heats one boat larger).
     */
    distributeIntoHeats(ordered: EventBoat[], mode: string): EventBoat[][] {
        const heats = this.eliminationHeats
        const groups: EventBoat[][] = Array.from({ length: heats }, () => [])

        if (mode === 'round-robin') {
            ordered.forEach((boat, i) => {
                groups[i % heats].push(boat)
            })
        } else if (mode === 'snake') {
            const cycle = 2 * heats
            ordered.forEach((boat, i) => {
                const p = i % cycle
                groups[p < heats ? p : cycle - 1 - p].push(boat)
            })
        } else {
            // 'blocks' (default): consecutive chunks, lower-numbered heats a boat larger.
            const base = Math.floor(ordered.length / heats)
            const extra = ordered.length % heats
            let idx = 0
            for (let h = 0; h < heats; h++) {
                const size = base + (h < extra ? 1 : 0)
                groups[h] = ordered.slice(idx, idx + size)
                idx += size
            }
        }

        return groups
    }

    // Stable non-cryptographic hash of a string, used to produce a deterministic pseudo-random order.
    static __idHash(value: string): number {
        let hash = 0
        for (let i = 0; i < value.length; i++)
            hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0
        return hash
    }

    get isNationalChampionship(): boolean {
        const coefId = String(this.weight) + (this.eventType ?? '')
        const coef = useRepo(EventCoefficient).find(coefId)
        return coef?.isNationalChampionship ?? false
    }

    get eventClassName() {
        return this.className ?? this.boatClass?.name
    }

    get publicMode() {
        return this.publicLicenseExceptionType ?? 0
    }

    get eventTypeDesc() {
        if (this.eventType != null && this.eventType !== '') {
            const coef = useRepo(EventCoefficientsRepo).where('value', this.weight).where('shortcut', this.eventType)
                .first()

            if (coef !== null)
                return coef.name
        }

        const coef = useRepo(EventCoefficientsRepo).where('value', this.weight).first()

        if (coef !== null)
            return coef.name

        return String(this.weight) + (this.eventType != null ? this.eventType : '')
    }

    get eventTypeNormalized() {
        return String(this.weight) + (this.eventType != null ? this.eventType : '')
    }

    set eventTypeNormalized(val) {
        const weight = val.replaceAll(/\D+/g, '')
        const eventTypeChar = val.replace(/^\d+/, '')

        this.weight = parseInt(weight)
        this.eventType = eventTypeChar === '' ? null : eventTypeChar
        this.shortcut = this.boatClass.shortcut + '-' + String(weight) + (eventTypeChar != null ? eventTypeChar : '')

        useRepo(EventClass).save(this)

        this.event?.setModified(true)
    }

    set eventClassName(val) {
        if (!this.boatClass.canBeRenamed)
            return

        this.className = val

        useRepo(EventClass).save(this)

        this.event?.setModified(true)
    }

    get boats(): EventBoat[] {
        const event: Event | null | undefined = this.event

        if (event === undefined || event === null || event.boats === undefined)
            return useRepo(EventBoat)
                .where('eventId', this.eventId)
                .where('boatClassId', this.boatClassId)
                .with('crew', (q) => {
                    q.with('member', (qm) => {
                        qm.with('club')
                    })
                })
                .get()

        return event.boats.filter((boat: EventBoat) => boat.boatClassId === this.boatClassId)
    }

    get areAllBoatsValid() {
        const boats = this.boats

        return boats.length > 0 && boats.filter(boat => !boat.isValid).length === 0
    }

    get totalBoats() {
        return this.boats.length
    }

    get totalRacedBoats() {
        return this.cachedTotalRacedBoats()
    }

    cachedTotalRacedBoats(availableRaces: EventRace[] | null = null) {
        if (availableRaces === null) {
            const cacheKey = `totalRacedBoats|${this.eventId}|${this.boatClassId}`

            if (EventClass.__cacheMap.has(cacheKey))
                return EventClass.__cacheMap.get(cacheKey) as number

            availableRaces = this.event.availableRaces(this.boatClassId)

            const res = this.boats.filter(boat => boat.cachedRaced(availableRaces)).length

            EventClass.__cacheMap.set(cacheKey, res)

            return res
        }

        return this.boats.filter(boat => boat.cachedRaced(availableRaces)).length
    }

    get raced() {
        return this.cachedRaced()
    }

    cachedRaced(availableRaces: EventRace[] | null = null) {
        if (availableRaces === null) {
            const cacheKey = `raced|${this.eventId}|${this.boatClassId}`

            if (EventClass.__cacheMap.has(cacheKey))
                return EventClass.__cacheMap.get(cacheKey) as number

            availableRaces = this.event.availableRaces(this.boatClassId, false)

            const res = availableRaces.length

            EventClass.__cacheMap.set(cacheKey, res)

            return res
        }

        return availableRaces.length
    }

    get calculatedWeight() {
        return this.cacheCalculatedWeight()
    }

    cacheCalculatedWeight(availableRaces = null) {
        if (availableRaces === null)
            availableRaces = this.event.availableRaces(this.boatClassId)

        const raced = this.cachedRaced(availableRaces)
        const boats = this.cachedTotalRacedBoats(availableRaces)

        if (this.weight >= 8 && boats >= 10 && (this.isLongDistance || raced >= 3))
            return this.weight

        if (this.weight >= 3 && boats >= 5 && (this.isLongDistance || raced >= 3))
            return this.weight >= 8 ? 7 : this.weight

        if (this.weight >= 2 && boats >= 5 && (this.isLongDistance || raced >= 2))
            return 2

        if (this.weight >= 1 && boats >= 3 && (this.isLongDistance || raced >= 1))
            return 1

        return 0
    }

    get discarded() {
        /**
         * Discard definition can use different formulas:
         * 0,0,0,1,1,1,1,2 (as of SailWave, more races have same as highest number of discarded races)
         * R4,R8 (at what race number is number of discarded races increment)
         * magical formula with "s" (number of sailed races and how to calculare number of discarded races, truncated to floor), e.g. "0.8 * ( s - 1 )"
         * ditto with "r" for number of planned races
         */

        const definition = this.discardDefinition.trim()

        const raced = this.raced

        if (definition.includes(',')) {
            let partDefinitions = definition.split(',').map(item => item.trim().toUpperCase())

            if (partDefinitions[0].charAt(0) === 'R') {
                let generated = 0
                let current = 0

                const result = partDefinitions.sort((a, b) => parseInt(a.substring(1)) - parseInt(b.substring(1)))
                    .reduce<number[]>((accumulator, def) => {
                        const race = def.substring(1)
                        if (!Number.isNaN(+race)) {
                            const newOne = Array(+race - generated - 1).fill(current)

                            generated += newOne.length
                            ++current

                            return [...accumulator, ...newOne]
                        }

                        return accumulator
                    }, [])

                if (current <= partDefinitions.length)
                    result.push(current)

                partDefinitions = result.map(item => String(item))
            }

            if (partDefinitions.length < raced)
                return partDefinitions[partDefinitions.length - 1]

            return partDefinitions[raced - 1]
        }

        if (definition.charAt(0) === 'R') {
            const race = definition.substring(1)

            if (!Number.isNaN(+race))
                return (this.raced < +race) ? 0 : 1
        }

        const sandbox = new Sandbox()
        const exec = sandbox.compile('return ' + definition)

        const result = exec({
            r: (this.event.plannedRaces !== undefined && this.event.plannedRaces !== null)
                ? this.event.plannedRaces
                : undefined,
            s: raced,
        }).run()

        if (!Number.isNaN(Number(result)))
            return result

        return 0
    }

    get maxCrew() {
        return Math.max(0, ...this.boats.map(b => b.crew.length))
    }

    get isBoatNamesUsed() {
        return this.boats.some(b => b.name !== null && b.name !== undefined && b.name?.trim() !== '')
    }

    get orderedCategories() {
        return this.categories.sort((a, b) => a.visibleTitle.localeCompare(b.visibleTitle))
    }

    static piniaOptions = {
        persist: false,
    }
}
